/** Doctor API (api-contract.md §4): profile, prescriptions, patient lookup. */

import { apiFetch, newIdempotencyKey } from "./client";
import type { Prescription } from "./patient";

export type { PaginatedPrescriptions, Prescription, RxLine } from "./patient";

export interface DoctorMe {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  status: string;
  state_medical_council: string | null;
  specialisation: string | null;
  license_number_masked: string;
  verified_at: string | null;
}

export const getDoctorMe = (): Promise<DoctorMe> => apiFetch("/api/v1/doctor/me");

// Doctors can edit their own display name via the shared /me/profile endpoint (D-04).
export const updateDoctorName = (full_name: string): Promise<{ full_name: string }> =>
  apiFetch("/api/v1/me/profile", { method: "PATCH", body: { full_name } });

export interface RxLineInput {
  medicine_id: string;
  quantity: number;
  /** A decimal as text ("1.5"), so the dose reaches the API exactly as typed. */
  dosage_value: string;
  dosage_unit: string;
  food_relation: string;
  time_of_day: string[];
  duration_days?: number | null;
}

export interface RxCreateInput {
  patient_account_id: string;
  for_family_member_id?: string | null;
  notes?: string | null;
  expires_at_override?: string | null;
  lines: RxLineInput[];
}

export const createPrescription = (body: RxCreateInput, idempotencyKey?: string): Promise<Prescription> =>
  apiFetch("/api/v1/doctor/prescriptions", {
    method: "POST",
    body,
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });

/** A row of the doctor's Rx list: an Rx they wrote, or one digitised under their name. */
export interface DoctorRxSummary {
  id: string;
  prescription_id: string; // human reference, e.g. RX-12
  for_family_member_id: string | null;
  schedule_class: string; // strictest across the lines: none | H | H1 | X
  notes: string | null;
  issued_at: string;
  expires_at: string;
  status: string; // derived lifecycle: active | expired | consumed
  is_digitized: boolean; // a pharmacist digitised it from the patient's paper upload
  line_count: number;
  patient_name: string | null; // the family member's when the Rx is for one
  patient_phone: string | null; // always the account holder's
}

export interface PaginatedDoctorRx {
  data: DoctorRxSummary[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export interface DoctorRxQuery {
  q?: string; // Rx reference, notes, patient name, medicine, or 3+ digits of the patient's phone
  status?: "active" | "expired" | "consumed";
  schedule_class?: "none" | "H" | "H1" | "X";
  source?: "written" | "digitized";
  issued_from?: string; // ISO instant, inclusive
  issued_to?: string; // ISO instant, exclusive
  sort?: "issued_at" | "expires_at" | "patient_name";
  descending?: boolean; // the API defaults to true (newest first)
  page?: number;
  per_page?: number;
}

/** The doctor's Rx, searched, filtered, sorted and paged by the server (D-02). */
export const listDoctorPrescriptions = (
  query: DoctorRxQuery = {},
  signal?: AbortSignal,
): Promise<PaginatedDoctorRx> => {
  const params = new URLSearchParams();
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.status) params.set("status", query.status);
  if (query.schedule_class) params.set("schedule_class", query.schedule_class);
  if (query.source) params.set("source", query.source);
  if (query.issued_from) params.set("issued_from", query.issued_from);
  if (query.issued_to) params.set("issued_to", query.issued_to);
  if (query.sort) params.set("sort", query.sort);
  if (query.descending === false) params.set("descending", "false");
  params.set("page", String(query.page ?? 1));
  params.set("per_page", String(query.per_page ?? 25));
  return apiFetch(`/api/v1/doctor/prescriptions?${params.toString()}`, { signal });
};

export const getDoctorPrescription = (id: string): Promise<Prescription> =>
  apiFetch(`/api/v1/doctor/prescriptions/${id}`);

export const editPrescription = (
  id: string,
  body: { notes?: string; expires_at_override?: string | null; lines?: RxLineInput[] },
): Promise<Prescription> => apiFetch(`/api/v1/doctor/prescriptions/${id}`, { method: "PATCH", body });

// Patient lookup (to write an Rx for)

export interface PatientSearchResult {
  id: string;
  full_name: string;
  phone: string | null;
}

export const searchPatients = (q: string, signal?: AbortSignal): Promise<PatientSearchResult[]> =>
  apiFetch(`/api/v1/doctor/patients/search?q=${encodeURIComponent(q.trim())}`, { signal });

export interface PatientEnsureResult {
  id: string;
  full_name: string;
  phone: string | null;
  created: boolean;
}

// Find a patient by phone, creating a new patient account if none exists (for walk-ins).
export const ensurePatient = (body: {
  phone: string;
  full_name: string;
}): Promise<PatientEnsureResult> =>
  apiFetch("/api/v1/doctor/patients/ensure", { method: "POST", body });

export interface PatientFamilyMini {
  id: string;
  full_name: string;
  relationship: string;
}

export const getPatientFamily = (patientId: string): Promise<PatientFamilyMini[]> =>
  apiFetch(`/api/v1/doctor/patients/${patientId}/family`);
